import { Box, Flex, PageWrapper, TutorialProvider, useLayout } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { hocs as pageHocs } from 'modules/page';
import { memo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { amplitudeService } from 'services/amplitude';
import { notUpgradableCompanyPlans } from 'shared/constants';

import { pageConfig } from './config';
import Loading from './loading';
import {
    Cin7WorkflowList,
    IntegrationConnection,
    NetSuiteWorkflowList,
    QBooksWorkflowList,
    StandaloneUpgrade,
    StandaloneWorkflowList,
    XeroWorkflowList,
} from './resources/components';
import { createTemplateButtonDataQa } from './resources/components/StandaloneWorkflowList/StandaloneWorkflowList.constans';
import { useBreadcrumbs } from './resources/hooks/useBreadcrumbs';
import { useWorkflowsListPageData } from './resources/hooks/useWorkflowsListPageData';
import { messages } from './resources/messages';

const Content = memo(() => {
    const company = useSelector(selectors.navigation.getActiveCompany);
    const user = useSelector(selectors.profile.getProfileUser);
    const userRole = selectors.company.getCompanyUserRole(company, user.id);
    const integration = useSelector((state: State) =>
        selectors.integration.getIntegrationByCompanyId(state, company.id)
    );
    const explainedIntegrationState = selectors.integration.getExplainedIntegrationState(integration);
    const standaloneTemplates = useSelector((state: State) =>
        selectors.template.getStandaloneTemplatesByCompanyId(state, company.id)
    );

    useBreadcrumbs(company);

    useEffect(() => {
        amplitudeService.sendData('workflows list: view list', {
            'connection type': integration?.integrationType.toLocaleLowerCase(),
        });
    }, [integration?.integrationType]);

    const isActiveIntegration =
        integration?.integrationType &&
        ((integration && integration.error) ||
            explainedIntegrationState.isConnectingFinalizing ||
            !explainedIntegrationState.neverConnectedBefore);
    const isAvailableStandalone = company.availableStandaloneTemplates;
    const isActiveStandalone = isAvailableStandalone && standaloneTemplates.length > 0;
    const isCanBeUpgraded = !notUpgradableCompanyPlans.includes(company.licenseProductPlan);

    const isFocusOnLeftColumn = Boolean(
        isActiveIntegration ? !isAvailableStandalone || !standaloneTemplates.length : !isActiveStandalone
    );

    const isRightColumnEnabled = userRole !== domain.CompanyUserRole.Auditor || !isFocusOnLeftColumn;

    const isSomeThingToShowInRightColumn =
        isRightColumnEnabled && (isActiveStandalone || isAvailableStandalone || isCanBeUpgraded);

    return (
        <PageWrapper>
            <Helmet title={messages.title({ companyName: company.displayName })} />

            <PageWrapper.Content>
                <Flex container spacing='16'>
                    <Flex size={isFocusOnLeftColumn ? 15 : 12} block>
                        <Box bordered radius='small'>
                            {isActiveIntegration ? (
                                {
                                    [domain.IntegrationType.Xero]: <XeroWorkflowList />,
                                    [domain.IntegrationType.QBooks]: <QBooksWorkflowList />,
                                    [domain.IntegrationType.NetSuite]: <NetSuiteWorkflowList />,
                                    [domain.IntegrationType.Dear]: <Cin7WorkflowList />,
                                    [domain.IntegrationType.None]: null,
                                }[integration?.integrationType]
                            ) : (
                                <>{isActiveStandalone ? <StandaloneWorkflowList /> : <IntegrationConnection />}</>
                            )}
                        </Box>
                    </Flex>

                    {isSomeThingToShowInRightColumn && (
                        <Flex size={isFocusOnLeftColumn ? 9 : 12} block>
                            <Box bordered radius='small'>
                                {isActiveStandalone ? (
                                    <>{isActiveIntegration ? <StandaloneWorkflowList /> : <IntegrationConnection />}</>
                                ) : (
                                    <>
                                        {isAvailableStandalone ? (
                                            <TutorialProvider
                                                selector={`[data-qa="${createTemplateButtonDataQa}"]`}
                                                content={messages.tutorialContent}
                                            >
                                                {({ openTour }) => <StandaloneWorkflowList openTour={openTour} />}
                                            </TutorialProvider>
                                        ) : (
                                            <>{isCanBeUpgraded && <StandaloneUpgrade />}</>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Flex>
                    )}
                </Flex>
            </PageWrapper.Content>
        </PageWrapper>
    );
});

const Page = memo(() => {
    const { companyId } = useParams<{ companyId: string }>();
    const activeCompanyId = useSelector(selectors.navigation.getActiveCompanyId);

    useLayout({ hasHeader: true, hasDrawer: true });

    const { isInitialLoading } = useWorkflowsListPageData();

    if (isInitialLoading || activeCompanyId !== companyId) {
        return <Loading />;
    }

    return <Content />;
});

export default pageHocs.withProtected({ Page, roles: pageConfig.access });
